import type { AssessmentType } from '../apiClient';

/**
 * Central route paths for the free journey (wouter). A single `/results/:id` route
 * renders the correct per-type view from the fetched assessmentType, which removes
 * the Replit per-type result URLs and the Perspective claim-redirect defect.
 */
/** The two workbook spans legacy delivers. Work Fit has neither. */
export type WorkbookSpan = '7-day' | '30-day';

export function isWorkbookSpan(value: string): value is WorkbookSpan {
  return value === '7-day' || value === '30-day';
}

export const ASSESSMENT_LABELS: Record<AssessmentType, string> = {
  bottleneck: 'Bottleneck Assessment',
  work_fit: 'Work Fit Assessment',
  perspective: 'Perspective Assessment',
};

export const routes = {
  home: '/',
  /**
   * The authenticated home (U1a). Answers PO-12: the shell's account
   * affordance needed one obvious route back into the visitor's own material,
   * and until now the only candidate was the report library, which is a single
   * surface rather than a home.
   */
  dashboard: '/dashboard',
  /**
   * The administrative console (A4a). Guarded by `RequireAdmin`, which is a
   * courtesy rather than the boundary — the server refuses the underlying data
   * regardless of what the browser renders.
   *
   * **The only admin route.** Production serves the entire console from this
   * one path and answers Not Found for every sibling — `/admin/users`,
   * `/admin/customers`, `/admin/reviews` and the rest (spec §1). There was an
   * `/admin/customers` here; the migration retired it, because a route
   * production does not serve is a behaviour V2 would have to explain rather
   * than reproduce.
   */
  admin: '/admin',
  assessment: (type: AssessmentType): string => `/assessment/${type}`,
  resultsReady: '/results/ready',
  welcome: '/welcome',
  result: (id: string): string => `/results/${id}`,
  // Paid-report journey (V2-015).
  reports: '/reports',
  report: (id: string): string => `/reports/${id}`,
  reportPrint: (id: string): string => `/reports/${id}/print`,
  /**
   * Standalone workbook pages (C1). Legacy served four routes — a 7-day and a
   * 30-day under both `/report/:id` and `/perspective/report/:id`. V2 already
   * unifies reports under one `/reports/:id` that dispatches on the payload's
   * `assessmentType`, so two patterns carry all four workbooks. That is the same
   * adaptation `/results/:id` and `/reports/:id` already made.
   */
  reportWorkbook: (id: string, span: WorkbookSpan): string =>
    `/reports/${id}/workbook/${span}`,
  // Public website (V2-018C). Paths only. The Experience Shell's navigation
  // needs destinations, so these constants land with the shell slice; the pages
  // themselves arrive in the later V2-018C page slices.
  why: '/why',
  bottleneck: '/bottleneck',
  workFit: '/work-fit',
  perspective: '/perspective',
  // Legal pages, migrated from the legacy app and served natively here.
  privacy: '/privacy',
  terms: '/terms',
  // Password recovery (V2-018). `resetPassword` requests the email;
  // `updatePassword` is the target of the emailed link and is the value passed
  // as Supabase's `redirectTo`, so it must stay in the Auth redirect allowlist.
  resetPassword: '/reset-password',
  updatePassword: '/update-password',
};

const ASSESSMENT_TYPES: AssessmentType[] = ['bottleneck', 'work_fit', 'perspective'];

export function isAssessmentType(value: string): value is AssessmentType {
  return ASSESSMENT_TYPES.includes(value as AssessmentType);
}
